<template>
  <div>
    <div class="intro-y flex items-center mt-8">
      <div class="estimation-heading mr-auto">
        <h2 class="text-lg font-medium">{{ pageTitle }}</h2>
        <div class="estimation-heading-details">
          <span>{{ headerEstimationNo }}</span>
          <span>{{ headerDate }}</span>
          <span>{{ form.manager_name || '-' }}</span>
          <span class="estimation-status-control">
            <select v-model="form.status" class="form-select" :disabled="isView || isExpired" aria-label="Estimation status" @change="confirmStatusChange">
              <option value="1">Active</option>
              <option value="0">Inactive</option>
              <option v-if="isExpired" value="2">Expired</option>
            </select>
          </span>
        </div>
      </div>
      <router-link :to="{ name: 'estimation-list' }" class="btn btn-outline-secondary w-24 mr-2">Back</router-link>
      <button v-if="!isView && !isExpired" type="button" class="btn btn-primary" :disabled="page.submit" @click="saveEstimation">
        Save Estimation
        <LoadingIcon v-if="page.submit" icon="oval" color="white" class="w-4 h-4 ml-2" />
      </button>
    </div>

    <div v-if="!isView && !isExpired" ref="entryFormRef" class="intro-y box p-5 mt-5">
      <div>
        <parameter-options title="Shape" field="shape_id" label-field="shape_name" :items="masters.shapes" :row="detailForm" @change="loadRepoRate" />
        <parameter-options title="Color" field="color_id" label-field="color_name" :items="masters.colors" :row="detailForm" @change="loadRepoRate" />
        <parameter-options title="Clarity" field="clarity_id" label-field="purity_name" :items="masters.clarities" :row="detailForm" @change="loadRepoRate" />
        <parameter-options title="Cut" field="cut_id" label-field="cut_name" :items="masters.cuts" :row="detailForm" />
        <div class="parameter-pair">
          <parameter-options title="Polish" field="polish_id" label-field="polish_name" :items="masters.polishes" :row="detailForm" compact />
          <parameter-options title="Symmetry" field="symmetry_id" label-field="symmetry_name" :items="masters.symmetries" :row="detailForm" compact />
        </div>
        <parameter-options title="Fluorescence" field="fluorescence_id" label-field="fluerocent_name" :items="masters.fluorescences" :row="detailForm" />
        
        <parameter-options title="Inclusion White" field="inclusion_white_id" label-field="name" :items="masters.inclusionWhites" :row="detailForm" />
        <parameter-options title="Inclusion Black" field="inclusion_black_id" label-field="name" :items="masters.inclusionBlacks" :row="detailForm" />
        <parameter-options title="Table Open" field="table_open_id" label-field="name" :items="masters.tableOpens" :row="detailForm" />
        <div class="parameter-pair">
          <parameter-options title="Side Open" field="side_open_id" label-field="name" :items="masters.sideOpens" :row="detailForm" compact />
          <parameter-options title="Milky" field="milky_id" label-field="name" :items="masters.milkies" :row="detailForm" compact />
        </div>
        <div class="grid grid-cols-12 gap-4 py-4 border-b border-gray-300 estimation-entry-fields">
          <div class="col-span-12 md:col-span-2 xl:col-span-1">
            <label class="form-label">Weight</label>
            <input v-model="detailForm.weight" type="number" min="0.001" step="0.001" class="form-control" :class="{ 'border-theme-6': page.rowErrors.weight }" @input="loadRepoRate" @blur="normalizePositiveWeight" />
            <div v-if="page.rowErrors.weight" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.weight[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-2 xl:col-span-2">
            <label class="form-label">Repo</label>
            <input v-model="detailForm.repo" type="number" step="0.01" class="form-control" :class="{ 'border-theme-6': page.rowErrors.repo }" disabled />
            <div v-if="page.rowErrors.repo" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.repo[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-2 xl:col-span-1">
            <label class="form-label">Discount</label>
            <input v-model="detailForm.discount" type="number" step="0.01" class="form-control" :class="{ 'border-theme-6': page.rowErrors.discount }" @input="calculatePriceFromRepo" />
            <div v-if="page.rowErrors.discount" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.discount[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-2">
            <label class="form-label">Price/Ct</label>
            <input v-model="currentTag.price_per_ct" type="number" step="0.01" class="form-control" :class="{ 'border-theme-6': page.rowErrors.price_per_ct }" @input="calculateDiscountFromPrice" />
            <div v-if="page.rowErrors.price_per_ct" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.price_per_ct[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-2">
            <label class="form-label">Total $</label>
            <input :value="formatNumber(detailForm.total, 2)" type="text" class="form-control" disabled />
          </div>
          <div class="col-span-12 md:col-span-2 xl:col-span-1">
            <label class="form-label">Tag</label>
            <input v-model="currentTag.tag" type="text" maxlength="1000" class="form-control" :class="{ 'border-theme-6': page.rowErrors.tag }" @change="getPacketDetails" />
            <div v-if="page.rowErrors.tag" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.tag[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-3 xl:col-span-2">
            <label class="form-label">Packet No.</label>
            <input v-model="currentTag.packet_no" type="text" class="form-control" :class="{ 'border-theme-6': page.rowErrors.packet_no }" />
            <div v-if="page.rowErrors.packet_no" class="text-theme-6 text-xs mt-1">{{ page.rowErrors.packet_no[0] }}</div>
          </div>
          <div class="col-span-12 md:col-span-3 xl:col-span-1 estimation-save-field">
            <button type="button" class="btn btn-primary w-full" :disabled="page.rowSubmit" @click="saveRow">
              {{ editingRowId ? 'Update' : 'Save' }}
              <LoadingIcon v-if="page.rowSubmit" icon="oval" color="white" class="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

    </div>

    <div class="intro-y box p-3 mt-5">
      <div class="overflow-x-auto estimation-grid">
        <table class="table whitespace-nowrap">
          <thead>
            <tr>
              <th v-if="!isView" class="text-center">Action</th>
              <th>Tag</th>
              <th>Packet No.</th>
              <th>Shape</th>
              <th>Color</th>
              <th>Purity</th>
              <th>Cut</th>
              <th>Polish</th>
              <th>Sym</th>
              <th>Fluorescence</th>
              <th class="text-right">Weight</th>
              <th class="text-right">Repo</th>
              <th class="text-right">Disc.</th>
              <th class="text-right">Price/Ct</th>
              <th class="text-right">Total $</th>
              <th class="text-right">Avg/Ct</th>
              <th>Inclusion White</th>
              <th>Inclusion Black</th>
              <th>Table Open</th>
              <th>Side Open</th>
              <th>Milky</th>
             
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in gridRows" :key="row.local_id" :class="{ 'tag-group-start': row.group_row_index === 0 }">
              <td v-if="!isView" class="text-center">
                <button type="button" class="btn btn-sm btn-primary" @click="editRow(row)">Edit</button>
              </td>
              <td v-if="row.group_row_index === 0" :rowspan="row.group_rowspan" class="tag-group-cell">{{ row.tag }}</td>
              <td v-if="row.packet_group_row_index === 0" :rowspan="row.packet_group_rowspan" class="packet-group-cell">
                <span class="packet-number">{{ row.packet_no || '-' }}</span>
                <button
                  v-if="!isView"
                  type="button"
                  class="btn btn-sm btn-outline-primary add-plan-btn"
                  title="Add In Plan"
                >
                  Add In Plan
                </button>
              </td>
              <td>{{ row.shape_name || '-' }}</td>
              <td>{{ row.color_name || '-' }}</td>
              <td>{{ row.clarity_name || '-' }}</td>
              <td>{{ row.cut_name || '-' }}</td>
              <td>{{ row.polish_name || '-' }}</td>
              <td>{{ row.symmetry_name || '-' }}</td>
              <td>{{ row.fluorescence_name || '-' }}</td>
              <td class="text-right">{{ formatNumber(row.weight, 3) }}</td>
              <td class="text-right">{{ formatNumber(row.repo, 2) }}</td>
              <td class="text-right">{{ formatNumber(row.discount, 2) }}</td>
              <td class="text-right">{{ formatNumber(row.price_per_ct, 2) }}</td>
              <td class="text-right">{{ formatNumber(row.total, 2) }}</td>
              <td v-if="row.group_row_index === 0" :rowspan="row.group_rowspan" class="text-right tag-average-cell">{{ formatNumber(row.tag_avg_per_ct, 2) }}</td>
              <td>{{ row.inclusion_white_name || '-' }}</td>
              <td>{{ row.inclusion_black_name || '-' }}</td>
              <td>{{ row.table_open_name || '-' }}</td>
              <td>{{ row.side_open_name || '-' }}</td>
              <td>{{ row.milky_name || '-' }}</td>
            </tr>
            <tr v-if="gridRows.length === 0">
              <td :colspan="isView ? 20 : 21" class="text-center text-gray-600 py-4">No data</td>
            </tr>
          </tbody>
          <tfoot v-if="gridRows.length">
            <tr class="font-medium">
              <td :colspan="isView ? 9 : 10" class="text-right">Total</td>
              <td class="text-right">{{ formatNumber(totals.total_weight, 3) }}</td>
              <td colspan="3"></td>
              <td class="text-right">{{ formatNumber(totals.total_amount, 2) }}</td>
              <td class="text-right">{{ formatNumber(totals.avg_per_ct, 2) }}</td>
              <td colspan="5"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div id="estimation-success-notification" class="toastify-content hidden flex">
      <CheckCircleIcon class="text-theme-9" />
      <div class="ml-4 mr-4">
        <div class="font-medium" data-estimation-message></div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { computed, defineComponent, inject, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import router from '@/router'
import Toastify from 'toastify-js'
import { BASE_URL } from '@/constants/config'
import ParameterOptions from './ParameterOptions.vue'

const emptyDetail = () => ({
  shape_id: '',
  color_id: '',
  clarity_id: '',
  cut_id: '',
  polish_id: '',
  symmetry_id: '',
  fluorescence_id: '',
  inclusion_white_id: '',
  inclusion_black_id: '',
  table_open_id: '',
  side_open_id: '',
  milky_id: '',
  weight: '',
  repo: '',
  discount: '',
  total: 0
})

const emptyTag = () => ({
  local_id: Date.now(),
  tag_id: '',
  tag: '',
  packet_no: '',
  kapan: '',
  lot_no: '',
  price_per_ct: '',
  details: [],
  total_parts: 0,
  total_weight: 0,
  total_amount: 0
})

export default defineComponent({
  components: { ParameterOptions },
  setup() {
    const route = useRoute()
    const swal = inject('$swal')
    const isEdit = computed(() => route.name === 'estimation-edit' || route.name === 'estimation-view')
    const isView = computed(() => route.name === 'estimation-view')
    const isExpired = computed(() => String(form.status) === '2')
    const pageTitle = computed(() => isView.value ? 'View Estimation' : isEdit.value ? 'Edit Estimation' : 'Add Estimation')
    const page = reactive({
      submit: false,
      rowSubmit: false,
      errors: {},
      rowErrors: {}
    })
    const entryFormRef = ref()
    const editingRowId = ref(null)
    const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('user_details') || '{}')
    const form = reactive({
      id: '',
      estimation_no: '',
      estimation_date: new Date().toISOString().slice(0, 10),
      manager_name: user.first_name ? `${user.first_name} ${user.last_name || ''}` : '',
      status: '1',
      tags: []
    })
    const headerEstimationNo = computed(() => form.estimation_no || '...')
    const headerDate = computed(() => {
      if (!form.estimation_date) return '-'
      const [year, month, day] = form.estimation_date.split('-')
      return year && month && day ? `${day}-${month}-${year.slice(-2)}` : form.estimation_date
    })
    const confirmStatusChange = () => {
      const nextStatus = form.status
      const previousStatus = nextStatus === '1' ? '0' : '1'
      swal.fire({
        title: 'Are you sure?',
        text: nextStatus === '1'
          ? 'Do you want to active the estimation status?'
          : 'Do you want to inactive the estimation status?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then((result) => {
        if (!(result.isConfirmed || result.value)) {
          form.status = previousStatus
        }
      })
    }
    const currentTag = reactive(emptyTag())
    const detailForm = reactive(emptyDetail())
    const masters = reactive({
      shapes: [],
      colors: [],
      clarities: [],
      cuts: [],
      polishes: [],
      symmetries: [],
      fluorescences: [],
      inclusionWhites: [],
      inclusionBlacks: [],
      tableOpens: [],
      sideOpens: [],
      milkies: []
    })
    const totals = computed(() => {
      const totalWeight = form.tags.reduce((sum, tag) => sum + Number(tag.total_weight || 0), 0)
      const totalParts = form.tags.reduce((sum, tag) => sum + Number(tag.total_parts || 0), 0)
      const totalAmount = form.tags.reduce((sum, tag) => sum + Number(tag.total_amount || 0), 0)
      return {
        total_tags: form.tags.length,
        total_parts: totalParts,
        total_weight: totalWeight,
        total_amount: totalAmount,
        avg_per_ct: totalWeight > 0 ? totalAmount / totalWeight : 0
      }
    })
    const gridRows = computed(() => form.tags.flatMap((tag) => {
      return tag.details.map((row, rowIndex) => {
        return {
          ...row,
          tag_local_id: tag.local_id,
          group_row_index: rowIndex,
          group_rowspan: tag.details.length,
          packet_group_row_index: rowIndex,
          packet_group_rowspan: tag.details.length,
          tag: tag.tag,
          packet_no: tag.packet_no,
          tag_avg_per_ct: Number(tag.total_weight) > 0 ? Number(tag.total_amount) / Number(tag.total_weight) : 0,
          kapan: row.kapan !== undefined ? row.kapan : tag.kapan,
          lot_no: row.lot_no !== undefined ? row.lot_no : tag.lot_no,
          price_per_ct: tag.price_per_ct
        }
      })
    }))

    const formatNumber = (value, precision) => Number(value || 0).toFixed(precision)
    const api = (url, method = 'post', data = {}) => axios({
      url,
      method,
      baseURL: BASE_URL,
      data,
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      config: { headers: { Accept: 'application/json' } }
    })

    const getMaster = (url, target, normalizer, attempt = 0) => {
      api(url).then((res) => {
        const items = (res.data.items || res.data.data || []).filter((item) => item.status === undefined || item.status == 1 || item.active_status === 'Active').map(normalizer || ((item) => item))
        if (!items.length && attempt < 2) {
          setTimeout(() => getMaster(url, target, normalizer, attempt + 1), 500 * (attempt + 1))
          return
        }
        masters[target] = items
      }).catch(() => {
        if (attempt < 2) {
          setTimeout(() => getMaster(url, target, normalizer, attempt + 1), 500 * (attempt + 1))
        }
      })
    }

    const loadMasters = () => {
      getMaster('api/master/dp_shape', 'shapes')
      getMaster('api/master/dp_color', 'colors')
      getMaster('api/master/dp_purity', 'clarities')
      getMaster('api/master/dp_cut', 'cuts')
      getMaster('api/master/dp_polish', 'polishes')
      getMaster('api/master/dp_symmetry', 'symmetries')
      getMaster('api/master/dp_fluerocent', 'fluorescences')
      getMaster('api/master/dp_inclusion_white', 'inclusionWhites')
      getMaster('api/master/dp_inclusion_black', 'inclusionBlacks')
      getMaster('api/master/dp_table_open', 'tableOpens')
      getMaster('api/master/dp_side_open', 'sideOpens')
      getMaster('api/master/dp_milky', 'milkies')
    }

    const findName = (list, id, fields) => {
      const item = list.find((master) => String(master.id) === String(id))
      if (!item) return ''
      for (const field of fields) {
        if (item[field]) return item[field]
      }
      return item.name || item.short_code || ''
    }

    const calculateDetailTotal = () => {
      detailForm.total = Number(detailForm.weight || 0) * Number(currentTag.price_per_ct || 0)
    }

    let repoRateTimer = null
    let repoRateRequest = 0
    const loadRepoRate = () => {
      clearTimeout(repoRateTimer)
      const requestId = ++repoRateRequest
      calculateDetailTotal()
      const ready = detailForm.shape_id && detailForm.color_id && detailForm.clarity_id && Number(detailForm.weight) > 0
      if (!ready) {
        detailForm.repo = ''
        currentTag.price_per_ct = ''
        detailForm.total = 0
        return
      }
      repoRateTimer = setTimeout(() => {
        api('api/estimation/repo-rate', 'post', {
          shape_id: detailForm.shape_id,
          color_id: detailForm.color_id,
          clarity_id: detailForm.clarity_id,
          weight: detailForm.weight
        }).then((res) => {
          if (requestId !== repoRateRequest) return
          detailForm.repo = res.data.repo || ''
          calculatePriceFromRepo()
        }).catch(() => {
          if (requestId !== repoRateRequest) return
          detailForm.repo = ''
          currentTag.price_per_ct = ''
          detailForm.total = 0
        })
      }, 250)
    }

    const normalizePositiveWeight = () => {
      if (detailForm.weight === '') return
      const weight = Number(detailForm.weight || 0)
      if (weight <= 0) {
        detailForm.weight = ''
        detailForm.total = 0
      }
    }

    const calculatePriceFromRepo = () => {
      const repo = Number(detailForm.repo || 0)
      const discount = Number(detailForm.discount || 0)
      const hasDiscount = detailForm.discount !== '' && detailForm.discount !== null && detailForm.discount !== undefined
      if (repo > 0) {
        currentTag.price_per_ct = (hasDiscount ? repo + (repo * discount / 100) : repo).toFixed(2)
      }
      calculateDetailTotal()
    }

    const calculateDiscountFromPrice = () => {
      const repo = Number(detailForm.repo || 0)
      const pricePerCt = Number(currentTag.price_per_ct || 0)
      if (repo > 0 && currentTag.price_per_ct !== '' && currentTag.price_per_ct !== null && currentTag.price_per_ct !== undefined) {
        detailForm.discount = (((pricePerCt - repo) / repo) * 100).toFixed(2)
      }
      recalculateCurrentRows()
    }

    const recalculateTag = (tag) => {
      tag.details.forEach((row) => {
        row.total = Number(row.weight || 0) * Number(tag.price_per_ct || 0)
      })
      tag.total_parts = tag.details.length
      tag.total_weight = tag.details.reduce((sum, row) => sum + Number(row.weight || 0), 0)
      tag.total_amount = tag.details.reduce((sum, row) => sum + Number(row.total || 0), 0)
    }

    const recalculateCurrentRows = () => {
      calculateDetailTotal()
      const existing = form.tags.find((tag) => tag.local_id === currentTag.local_id)
      if (existing) {
        existing.price_per_ct = currentTag.price_per_ct
        recalculateTag(existing)
      }
    }

    const resetDetailForm = () => {
      Object.assign(detailForm, emptyDetail())
    }

    const resetCurrentTag = () => {
      Object.assign(currentTag, emptyTag())
      resetDetailForm()
    }

    const validateTagBase = () => {
      if (!currentTag.tag) return 'Tag is required.'
      if (Number(currentTag.price_per_ct || 0) <= 0) return 'Price/Ct must be greater than zero.'
      return ''
    }

    const addRow = () => {
      const baseError = validateTagBase()
      if (baseError) {
        showWarning(baseError)
        return false
      }
      if (Number(detailForm.weight || 0) <= 0) {
        showWarning('Weight must be greater than zero.')
        return false
      }
      calculateDetailTotal()
      const row = {
        ...JSON.parse(JSON.stringify(detailForm)),
        local_id: Date.now(),
        shape_name: findName(masters.shapes, detailForm.shape_id, ['shape_name']),
        color_name: findName(masters.colors, detailForm.color_id, ['color_name']),
        clarity_name: findName(masters.clarities, detailForm.clarity_id, ['purity_name']),
        cut_name: findName(masters.cuts, detailForm.cut_id, ['cut_name']),
        polish_name: findName(masters.polishes, detailForm.polish_id, ['polish_name']),
        symmetry_name: findName(masters.symmetries, detailForm.symmetry_id, ['symmetry_name']),
        fluorescence_name: findName(masters.fluorescences, detailForm.fluorescence_id, ['fluerocent_name']),
        inclusion_white_name: findName(masters.inclusionWhites, detailForm.inclusion_white_id, ['name']),
        inclusion_black_name: findName(masters.inclusionBlacks, detailForm.inclusion_black_id, ['name']),
        table_open_name: findName(masters.tableOpens, detailForm.table_open_id, ['name']),
        side_open_name: findName(masters.sideOpens, detailForm.side_open_id, ['name']),
        milky_name: findName(masters.milkies, detailForm.milky_id, ['name'])
      }
      let tag = form.tags.find((item) => item.local_id === currentTag.local_id)
      if (!tag) {
        tag = JSON.parse(JSON.stringify(currentTag))
        tag.local_id = Date.now()
        tag.details = []
        form.tags.push(tag)
        currentTag.local_id = tag.local_id
      }
      tag.tag_id = currentTag.tag_id
      tag.tag = currentTag.tag
      tag.packet_no = currentTag.packet_no
      tag.kapan = currentTag.kapan
      tag.lot_no = currentTag.lot_no
      tag.price_per_ct = currentTag.price_per_ct
      tag.details.push(row)
      recalculateTag(tag)
      resetDetailForm()
      return true
    }

    const addStoredRow = (storedRow) => {
      let tag = form.tags.find((item) => String(item.id || item.local_id) === String(storedRow.estimation_tag_id))
      if (!tag) {
        tag = {
          id: storedRow.estimation_tag_id,
          local_id: storedRow.estimation_tag_id,
          tag_id: storedRow.tag_id,
          tag: storedRow.tag,
          packet_no: storedRow.packet_no,
          kapan: storedRow.kapan,
          lot_no: storedRow.lot_no,
          price_per_ct: storedRow.price_per_ct,
          details: [],
          total_parts: 0,
          total_weight: 0,
          total_amount: 0
        }
        form.tags.push(tag)
      }
      tag.tag_id = storedRow.tag_id
      tag.tag = storedRow.tag
      tag.packet_no = storedRow.packet_no
      tag.price_per_ct = storedRow.price_per_ct
      tag.details.forEach((row) => {
        row.packet_no = storedRow.packet_no
      })
      tag.details.push({ ...storedRow, local_id: storedRow.id })
      recalculateTag(tag)
    }

    const saveRow = () => {
      page.rowErrors = {}
      if (!currentTag.tag) {
        page.rowErrors.tag = ['Tag is required.']
      }
      if (Number(currentTag.price_per_ct || 0) <= 0) {
        page.rowErrors.price_per_ct = ['Price/Ct must be greater than zero.']
      }
      if (Number(detailForm.weight || 0) <= 0) {
        page.rowErrors.weight = ['Weight must be greater than zero.']
      }
      if (Object.keys(page.rowErrors).length) return

      page.rowSubmit = true
      const isUpdatingRow = Boolean(editingRowId.value)
      const submittedEstimationId = form.id
      const url = editingRowId.value
        ? `api/estimation/update-row/${editingRowId.value}`
        : 'api/estimation/store-row'
      api(url, 'post', {
        estimation_id: form.id || null,
        estimation_date: form.estimation_date,
        status: form.status,
        tag_id: currentTag.tag_id || null,
        tag: currentTag.tag,
        packet_no: currentTag.packet_no || null,
        kapan: currentTag.kapan || null,
        lot_no: currentTag.lot_no || null,
        price_per_ct: currentTag.price_per_ct,
        ...JSON.parse(JSON.stringify(detailForm))
      }).then((res) => {
        page.rowSubmit = false
        form.id = res.data.estimation.id
        form.estimation_no = res.data.estimation.estimation_no
        const estimationWasVersioned = submittedEstimationId && String(submittedEstimationId) !== String(form.id)
        if (estimationWasVersioned) {
          router.replace({ name: 'estimation-edit', params: { id: form.id } })
          loadEstimation(form.id)
        } else if (editingRowId.value) {
          loadEstimation(form.id)
        } else {
          addStoredRow(res.data.row)
        }
        editingRowId.value = null
        resetDetailForm()
        currentTag.price_per_ct = ''
        showSuccess(isUpdatingRow ? 'Estimation updated successfully' : 'Estimation saved successfully')
      }).catch((err) => {
        page.rowSubmit = false
        page.rowErrors = err.response && err.response.data && err.response.data.errors ? err.response.data.errors : {}
        if (!Object.keys(page.rowErrors).length) {
          showWarning(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Unable to save estimation entry.')
        }
      })
    }

    const editRow = (row) => {
      editingRowId.value = row.id
      Object.assign(currentTag, {
        tag_id: row.tag_id || '',
        tag: row.tag || '',
        packet_no: row.packet_no || '',
        kapan: row.kapan || '',
        lot_no: row.lot_no || '',
        price_per_ct: row.price_per_ct || ''
      })
      Object.assign(detailForm, {
        shape_id: row.shape_id || '',
        color_id: row.color_id || '',
        clarity_id: row.clarity_id || '',
        cut_id: row.cut_id || '',
        polish_id: row.polish_id || '',
        symmetry_id: row.symmetry_id || '',
        fluorescence_id: row.fluorescence_id || '',
        inclusion_white_id: row.inclusion_white_id || '',
        inclusion_black_id: row.inclusion_black_id || '',
        table_open_id: row.table_open_id || '',
        side_open_id: row.side_open_id || '',
        milky_id: row.milky_id || '',
        weight: row.weight || '',
        repo: row.repo || '',
        discount: row.discount || '',
        total: row.total || 0
      })
      page.rowErrors = {}
      loadRepoRate()
      if (entryFormRef.value) entryFormRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const getPacketDetails = () => {
      if (!currentTag.tag) return
      api('api/estimation/packet-details', 'post', { tag: currentTag.tag }).then((res) => {
        const packet = res.data.packet || res.data.item || {}
        currentTag.tag_id = packet.id || currentTag.tag_id
        currentTag.packet_no = packet.number || packet.packet_no || currentTag.packet_no
        currentTag.kapan = packet.kapan || currentTag.kapan
        currentTag.lot_no = packet.lot_no || currentTag.lot_no
      }).catch(() => {})
    }

    const loadEstimation = (requestedId = null) => {
      const estimationId = requestedId || route.params.id || form.id
      if (!estimationId) return
      api(`api/estimation/detail/${estimationId}`, 'get').then((res) => {
        const item = res.data.item || res.data.estimation || {}
        form.id = item.id
        form.estimation_no = item.estimation_no
        form.estimation_date = item.estimation_date
        form.manager_name = item.manager_name || item.user_name || form.manager_name
        form.status = String(item.status ?? 1)
        form.tags = (item.tags || []).map((tag) => {
          const mapped = {
            ...tag,
            local_id: tag.id || Date.now() + Math.random(),
            details: (tag.details || []).map((row) => ({ ...row, local_id: row.id || Date.now() + Math.random() }))
          }
          recalculateTag(mapped)
          return mapped
        })
      }).catch(() => {})
    }

    const loadNextEstimationNo = () => {
      if (isEdit.value) return
      axios.get(BASE_URL + 'api/estimation/list', {
        params: { page: 1, size: 1 },
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') }
      }).then((res) => {
        const latest = (res.data.data || [])[0]
        const nextId = Number(latest && latest.id ? latest.id : 0) + 1
        form.estimation_no = String(nextId)
      }).catch(() => {
        form.estimation_no = '-'
      })
    }

    const saveEstimation = () => {
      if (!form.id || form.tags.length === 0) {
        showWarning('Add at least one tag before saving estimation.')
        return
      }
      page.submit = true
      page.errors = {}
      api(`api/estimation/finalize/${form.id}`, 'post', {
        estimation_date: form.estimation_date,
        status: form.status
      }).then((res) => {
        page.submit = false
        if (res.data.estimation && String(res.data.estimation.id) !== String(form.id)) {
          form.id = res.data.estimation.id
          form.estimation_no = res.data.estimation.estimation_no
        }
        showSuccess(isEdit.value ? 'Estimation updated successfully' : 'Estimation saved successfully')
        router.push({ name: 'estimation-list' })
      }).catch((err) => {
        page.submit = false
        page.errors = err.response && err.response.data && err.response.data.errors ? err.response.data.errors : {}
        showWarning(err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Something went wrong.')
      })
    }

    const showWarning = (message) => {
      Toastify({ text: message, duration: 3000, close: true, gravity: 'top', position: 'right', stopOnFocus: true }).showToast()
    }
    const showSuccess = (message) => {
      const notification = cash('#estimation-success-notification').clone().removeClass('hidden')
      notification.find('[data-estimation-message]').text(message)
      Toastify({
        node: notification[0],
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: 'top',
        position: 'right',
        stopOnFocus: true
      }).showToast()
    }

    onMounted(() => {
      loadMasters()
      if (isEdit.value) {
        loadEstimation()
      } else {
        loadNextEstimationNo()
      }
    })

    return {
      page,
      entryFormRef,
      editingRowId,
      form,
      currentTag,
      detailForm,
      masters,
      totals,
      gridRows,
      pageTitle,
      headerEstimationNo,
      headerDate,
      confirmStatusChange,
      isView,
      isExpired,
      formatNumber,
      saveRow,
      editRow,
      saveEstimation,
      resetDetailForm,
      recalculateCurrentRows,
      calculateDiscountFromPrice,
      calculateDetailTotal,
      normalizePositiveWeight,
      calculatePriceFromRepo,
      loadRepoRate,
      getPacketDetails
    }
  }
})
</script>

<style scoped>
.form-label {
  font-weight: 700;
}

.estimation-heading {
  display: flex;
  align-items: baseline;
  gap: 18px;
  min-width: 0;
}

.estimation-heading-details {
  display: flex;
  align-items: center;
  gap: 0;
  color: #475569;
  font-weight: 700;
}

.estimation-heading-details span + span::before {
  content: '/';
  margin: 0 8px;
  color: #94a3b8;
}

.estimation-status-control {
  display: inline-flex;
  align-items: center;
}

.estimation-status-control .form-select {
  width: 105px;
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
  font-weight: 700;
}

.parameter-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 18px;
  border-bottom: 1px solid #d9dee8;
}

.estimation-entry-fields .form-control {
  min-width: 0;
}

.estimation-save-field {
  align-self: end;
}

.estimation-grid table {
  min-width: 1880px;
  border-collapse: collapse !important;
  border-spacing: 0 !important;
  border: 1px solid #d7deea;
}

.estimation-grid th {
  background: #164e63;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 7px;
  border-right: 1px solid rgba(255, 255, 255, 0.16);
}

.estimation-grid td {
  border-right: 1px solid #e3e8ef;
  border-bottom: 1px solid #e3e8ef;
  font-size: 12px;
  padding: 5px 7px;
}

.estimation-grid tbody tr:nth-child(even) td {
  background: #f7fafb;
}

.estimation-grid tbody tr:not(:only-child):hover td {
  background: #edf6f8;
}

.estimation-grid .tag-group-start:not(:first-child) td {
  border-top: 2px solid #8fb1bd;
}

.estimation-grid .tag-group-cell {
  vertical-align: middle;
  background: #f0f6f8;
  color: #164e63;
  font-weight: 600;
}

.estimation-grid .packet-group-cell {
  vertical-align: middle;
  background: #f7fafb;
  color: #164e63;
  font-weight: 600;
}

.estimation-grid .packet-number {
  display: block;
  line-height: 1.2;
}

.estimation-grid .add-plan-btn {
  display: block;
  margin-top: 4px;
  padding: 2px 6px;
  border-color: #2f6f89;
  color: #245d74;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  box-shadow: none;
}

.estimation-grid .add-plan-btn:hover,
.estimation-grid .add-plan-btn:focus {
  border-color: #164e63;
  background: #164e63;
  color: #fff;
  box-shadow: none;
}

.estimation-grid .tag-average-cell {
  vertical-align: middle;
  background: #f0f6f8;
  color: #164e63;
  font-weight: 600;
}

.estimation-grid tfoot td {
  background: #e7f0f3;
  color: #164e63;
  padding-top: 6px;
  padding-bottom: 6px;
}

@media (max-width: 1023px) {
  .estimation-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .estimation-heading-details {
    flex-wrap: wrap;
  }

  .parameter-pair {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
